import { useCallback, useEffect, useState } from 'react';
import {
  fetchAdminClosureInvitations,
  sendAdminClosureInvitations,
  testAdminClosureInvitation,
} from '../infrastructure/adminApi';

const PLACE_LABELS = { 1: '1er lugar', 2: '2do lugar', 3: '3er lugar' };
const AUDIENCES = [
  {
    kind: 'general',
    title: 'Invitación general',
    description: 'Jugadores con correo verificado y suscritos a eventos. Los tres ganadores no la reciben.',
  },
  {
    kind: 'ganador',
    title: 'Invitación a ganadores',
    description: 'Sólo 1er, 2do y 3er lugar del ranking global: los invita a recoger su obsequio.',
  },
  {
    kind: 'erratas',
    title: 'Fe de erratas a ganadores',
    description: 'Corrige la invitación a ganadores: los premios sólo se entregan a quienes asistan a la clausura.',
  },
];
const inputClass = [
  'mt-1 min-h-[44px] w-full border border-[#3a3344] bg-[#0d0b11] px-3 py-2 font-mono text-sm normal-case',
  'tracking-normal text-white placeholder:text-[#6f6479]',
].join(' ');
const buttonClass = 'min-h-[44px] border px-4 py-2 text-[10px] font-black uppercase tracking-wider disabled:opacity-40';

// Envío masivo de invitaciones a la clausura. El backend manda los correos en
// segundo plano; este panel sólo lanza el envío y consulta el avance.
export default function ClosureInvitationsPanel({ authorization, onSessionExpired }) {
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const fail = useCallback((requestError) => {
    if (requestError.status === 401) onSessionExpired?.();
    else setError(requestError.message);
  }, [onSessionExpired]);

  const load = useCallback(async () => {
    try {
      const next = await fetchAdminClosureInvitations(authorization);
      setSummary(next);
      setDetails((current) => current || next.defaults);
    } catch (requestError) {
      fail(requestError);
    }
  }, [authorization, fail]);

  useEffect(() => { load(); }, [load]);

  // Mientras hay un envío corriendo, consulta el avance cada dos segundos.
  const running = Boolean(summary?.job?.running);
  useEffect(() => {
    if (!running) return undefined;
    const intervalId = window.setInterval(load, 2000);
    return () => window.clearInterval(intervalId);
  }, [running, load]);

  const sendTest = async (kind) => {
    setError('');
    setNotice('');
    setBusy(`test-${kind}`);
    try {
      const result = await testAdminClosureInvitation(authorization, { kind, to: testTo, details });
      setNotice(`Prueba enviada a ${result.to}.`);
    } catch (requestError) {
      fail(requestError);
    } finally {
      setBusy('');
    }
  };

  const sendAll = async (kind, pending) => {
    setError('');
    setNotice('');
    const audience = kind === 'general' ? 'toda la comunidad' : 'los ganadores';
    if (!window.confirm(
      `Se enviarán ${pending} correos a ${audience} con estos datos:\n\n`
      + `${details.fecha}, ${details.hora}\n${details.lugar}\n\nNo se puede deshacer. ¿Enviar?`,
    )) return;
    setBusy(`send-${kind}`);
    try {
      const { job } = await sendAdminClosureInvitations(authorization, { kind, details });
      setNotice(`Envío iniciado: ${job.total} correos en cola.`);
      await load();
    } catch (requestError) {
      fail(requestError);
    } finally {
      setBusy('');
    }
  };

  if (!summary || !details) {
    return (
      <section className="mx-auto max-w-[1480px] px-5 pb-10 lg:px-8">
        {error
          ? <p className="border border-[#F7E04B]/40 bg-[#2b1219] p-3 text-sm text-[#ffd8de]" role="alert">{error}</p>
          : <p className="font-mono text-xs text-[#8f829c]">Cargando invitaciones…</p>}
      </section>
    );
  }

  const { job } = summary;
  return (
    <section className="mx-auto max-w-[1480px] px-5 pb-10 lg:px-8">
      <div className="border border-[#7C3AED]/60 bg-[#141018] p-4">
        <p className="text-[10px] font-black uppercase tracking-[.16em] text-[#F7E04B]">Invitaciones a la clausura</p>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-[#b7a9c4]">
          Envío masivo por correo. Cada persona recibe una sola vez cada invitación: si relanzas el envío,
          sólo salen los pendientes y los que fallaron.
        </p>

        {error && <p className="mt-4 border border-[#F7E04B]/40 bg-[#2b1219] p-3 text-sm text-[#ffd8de]" role="alert">{error}</p>}
        {notice && <p className="mt-4 border border-[#7EC54A]/50 bg-[#0F1F12] p-3 text-sm text-[#C5E6A0]" role="status">{notice}</p>}

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[['fecha', 'Fecha'], ['hora', 'Hora'], ['lugar', 'Lugar']].map(([key, label]) => (
            <label key={key} className="text-[10px] font-black uppercase tracking-[.14em] text-[#9d8cab]">
              {label}
              <input
                type="text"
                value={details[key]}
                maxLength={120}
                onChange={(event) => setDetails({ ...details, [key]: event.target.value })}
                className={inputClass}
              />
            </label>
          ))}
        </div>

        <div className="mt-5">
          <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#9d8cab]">Ganadores del ranking global</p>
          {summary.winners.length ? (
            <ul className="mt-2 divide-y divide-[#1d1826] border border-[#312a3a] bg-[#0d0b11]">
              {summary.winners.map((winner) => (
                <li key={winner.email} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 text-sm text-[#eee8f5]">
                  <strong className="w-20 text-[#F7E04B]">{PLACE_LABELS[winner.place]}</strong>
                  <span className="font-bold">{winner.username}</span>
                  <span className="break-all font-mono text-xs text-[#b7a9c4]">{winner.email}</span>
                  <span className="ml-auto font-mono text-xs text-[#b7a9c4]">{winner.totalScore.toLocaleString('es-MX')} pts</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-[#8f829c]">Aún no hay jugadores con puntos en el ranking.</p>
          )}
        </div>

        <label className="mt-5 block max-w-sm text-[10px] font-black uppercase tracking-[.14em] text-[#9d8cab]">
          Correo para pruebas
          <input
            type="email"
            value={testTo}
            placeholder="tu@correo.com"
            onChange={(event) => setTestTo(event.target.value)}
            className={inputClass}
          />
        </label>

        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {AUDIENCES.map(({ kind, title, description }) => {
            const counts = summary[kind];
            return (
              <div key={kind} className="border border-[#312a3a] bg-[#0d0b11] p-4">
                <p className="font-display text-xl uppercase text-white">{title}</p>
                <p className="mt-1 text-sm leading-6 text-[#b7a9c4]">{description}</p>
                <p className="mt-3 font-mono text-xs text-[#d6c9df]">
                  {counts.total} destinatarios · {counts.sent} enviados · {counts.pending} pendientes
                  {counts.failed > 0 && <span className="text-[#ffd8de]"> · {counts.failed} con error</span>}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => sendTest(kind)}
                    disabled={Boolean(busy) || !testTo.trim()}
                    className={`${buttonClass} border-[#594a65] text-[#d6c9df]`}
                  >
                    {busy === `test-${kind}` ? 'Enviando…' : 'Enviar prueba'}
                  </button>
                  <button
                    type="button"
                    onClick={() => sendAll(kind, counts.pending)}
                    disabled={Boolean(busy) || running || counts.pending === 0}
                    className={`${buttonClass} border-[#F7E04B] bg-[#F7E04B] text-black`}
                  >
                    {counts.pending === 0 ? 'Todo enviado' : `Enviar a ${counts.pending}`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {job && (
          <p className="mt-4 font-mono text-xs text-[#C5E6A0]" role="status" aria-live="polite">
            {job.running ? 'Enviando' : 'Último envío'} ({job.kind}): {job.sent + job.failed} de {job.total}
            {job.failed > 0 ? ` · ${job.failed} con error` : ''}{job.running ? '…' : ' · terminado'}
          </p>
        )}
      </div>
    </section>
  );
}
